import { ReportValues } from './types';
import { v } from './schema';

/**
 * A composed report's layout: an ordered list of sections, each an ordered
 * list of blocks. It is one of the report's settings, written by the report's
 * author, so every run prints the same sections in the same order, and a
 * section that isn't in the layout simply isn't in the report.
 *
 * Numbering is the sheet's: numbered sections take 1, 2, 3… in the order they
 * print, and an unnumbered section (an appendix) prints its title alone.
 */

export interface LayoutBlock {
  /** stable within the layout; per-run fields are stored under it */
  id: string;
  /** the block type (blocks.ts) */
  type: string;
  props: ReportValues;
  /** `leave-out`: print nothing when the block has nothing to show for this run */
  whenEmpty?: 'show' | 'leave-out';
}

export interface LayoutSection {
  id: string;
  title: string;
  numbered: boolean;
  blocks: LayoutBlock[];
}

export interface ReportLayout {
  sections: LayoutSection[];
}

const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;

function normalizeBlock(raw: unknown, index: number, taken: Set<string>): LayoutBlock | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.type !== 'string' || !r.type) return null;
  let id = typeof r.id === 'string' && SAFE_ID.test(r.id) ? r.id : `${r.type}-${index + 1}`;
  while (taken.has(id)) id = `${id}-x`;
  taken.add(id);
  const props = r.props && typeof r.props === 'object' && !Array.isArray(r.props) ? (r.props as ReportValues) : {};
  const block: LayoutBlock = { id, type: r.type, props };
  if (r.whenEmpty === 'leave-out' || r.whenEmpty === 'show') block.whenEmpty = r.whenEmpty;
  return block;
}

/** Bring stored JSON up to a layout; anything unreadable falls back to the report's default. Block props are normalized by each block's own schema when the report reads them. */
export function normalizeLayout(raw: unknown, fallback: () => ReportLayout): ReportLayout {
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as { sections?: unknown }).sections)) return fallback();
  const sectionIds = new Set<string>();
  const blockIds = new Set<string>();
  const sections: LayoutSection[] = [];
  ((raw as { sections: unknown[] }).sections).forEach((s, i) => {
    if (!s || typeof s !== 'object') return;
    const r = s as Record<string, unknown>;
    let id = typeof r.id === 'string' && SAFE_ID.test(r.id) ? r.id : `section-${i + 1}`;
    while (sectionIds.has(id)) id = `${id}-x`;
    sectionIds.add(id);
    const blocks = Array.isArray(r.blocks) ? r.blocks.map((b, j) => normalizeBlock(b, j, blockIds)).filter((b): b is LayoutBlock => b !== null) : [];
    sections.push({ id, title: typeof r.title === 'string' ? r.title : '', numbered: r.numbered !== false, blocks });
  });
  return { sections };
}

/** The layout as a settings field. */
export const layoutField = (fallback: () => ReportLayout, options: { label?: string; help?: string } = {}) =>
  v.custom<ReportLayout>('layout', fallback, (raw) => normalizeLayout(raw, fallback), { label: options.label ?? 'Layout', help: options.help });

/** A fresh id for a section or block added to a layout. */
export function newLayoutId(prefix: string, layout: ReportLayout): string {
  const taken = new Set(layout.sections.flatMap((s) => [s.id, ...s.blocks.map((b) => b.id)]));
  for (let i = 1; ; i++) {
    const id = `${prefix}-${i}`;
    if (!taken.has(id)) return id;
  }
}
